import { ApiError } from "@hakili/api-client";
import {
  capacityEstimateBodySchema,
  capacityEstimateLenientReadSchema,
  type CapacityEstimateBody,
} from "@hakili/contracts";
import { formatMoney } from "@hakili/i18n";

/**
 * `DEALER-PREQUAL` (round A-D1, D-1031-6 item 3): **the dealer's prequalification screen's layer, « Combien le client peut-il financer ? ».** Bound to the merged contract
 * (`packages/contracts/src/capacity-estimate.ts`, `DEALER-PREQUAL-CONTRACT`, `P1-SIM-016`), not to a door: lane AA's door is not on main, so the screen reaches it through a seam
 * (`CapacityEstimateDoor`) that the session wires when the door exists. Pure, no React, no clock, no storage.
 *
 * **What the screen draws is what the door served and nothing else (D-1020-7).** Every figure is formatted, never computed; the caveat, the assumption lines and the rate range are the door's
 * words; the cars are the door's rows. The door now serves the cessible share as a RANGE (`ceilingPctRange`), each car's own instalment, band words and reste à vivre, and the cars it could not price (`withoutRate`): all are drawn as served, none computed. The bar is
 * GEOMETRY over served figures, shares of the salary the dealer typed, never money: its marks sit at the served range.
 *
 * **The salary is neither stored nor logged here** (the door keeps nothing either): no storage write, no console line, whatever the outcome.
 */

/** What the dealer typed, as text; the deposit and the duration are the chosen values. */
export interface EstimateInputs {
  readonly netSalary: string;
  readonly commitments: string;
  /** The deposit the estimate assumes, in percent. The contract names it `depositPct`, and ONLY this file says so (OD-37 R7, the recorded exception of 1 November 2026). */
  readonly deposit: number;
  readonly durationMonths: number;
}

/** The seam to lane AA's door: takes the contract's body, answers what the door answered (unparsed, so the screen holds the contract's reading, not the transport's). */
export type CapacityEstimateDoor = (body: CapacityEstimateBody) => Promise<unknown>;

export interface MoneyRange {
  readonly min: string;
  readonly max: string;
}

export interface AssumptionLine {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly source: string;
}

export interface CarLine {
  readonly vehicleId: string;
  readonly title: string;
  readonly price: string;
  /** THIS car's instalment at the best and the worst grade, as served. */
  readonly monthly: MoneyRange;
  /** The client's band code, which only COLOURS the line; the words are `effortBandLabel`, served. */
  readonly effortBand: "confortable" | "acceptable" | "au-dela";
  readonly effortBandLabel: string;
  readonly resteAVivre: MoneyRange;
}

/** A car placed by age that has no surviving rate at its own amount: its price and the door's sentence, never a figure. */
export interface WithoutRateLine {
  readonly vehicleId: string;
  readonly title: string;
  readonly price: string;
  readonly sentence: string;
}

/** Shares of the typed net salary, in percent: geometry, never money. */
export interface CapacityBar {
  readonly commitmentsShare: number;
  readonly monthlyMinShare: number;
  readonly monthlyMaxShare: number;
  /** The served cessible share, one mark per end of the range (lowest first, one mark when the ends are equal): the BANK's, never Hakili's 50 %. Served numbers, none computed. */
  readonly ceilingMarks: readonly number[];
}

interface EstimateBase {
  readonly chip: "estimation";
  readonly depositPct: number;
  readonly durationMonths: number;
  readonly caveat: string;
  readonly assumptions: readonly AssumptionLine[];
}

interface Figures extends EstimateBase {
  readonly financeable: MoneyRange;
  readonly monthly: MoneyRange;
  readonly ratePct: { readonly best: string; readonly worst: string };
  /** The bank's cessible share as the RANGE the door served across the active panel, never `ceilingPct` alone. */
  readonly ceiling: { readonly min: number; readonly max: number };
  readonly bar: CapacityBar;
  readonly withoutRate: readonly WithoutRateLine[];
}

export interface EstimateView extends Figures {
  readonly cars: { readonly kind: "within"; readonly parkCount: number; readonly within: readonly CarLine[] };
}

export interface NoCarView extends Figures {
  readonly cars:
    { readonly kind: "empty_park" } | { readonly kind: "none_within"; readonly parkCount: number };
}

export interface NoCapacityView extends EstimateBase {
  readonly capacity: string;
  readonly shortfall: string;
}

export type EstimateOutcome =
  | { readonly kind: "need"; readonly missing: "netSalary" | "commitments" }
  | { readonly kind: "estimate"; readonly view: EstimateView }
  | { readonly kind: "no_car_within_range"; readonly view: NoCarView }
  | { readonly kind: "no_capacity"; readonly view: NoCapacityView }
  | { readonly kind: "refused"; readonly words: string; readonly field: string | null }
  | { readonly kind: "failed" };

const SPACES = /[\s\u00a0\u202f\u2009]/gu;

/** A typed amount: whole digits, grouped with the spaces people write. Anything else is not a number (`null`). */
export function parseAmount(text: string): number | null {
  const plain = text.replace(SPACES, "");
  if (!/^\d+$/u.test(plain)) return null;
  return Number(plain);
}

const range = (value: { readonly min: number; readonly max: number }): MoneyRange => ({
  min: formatMoney(value.min),
  max: formatMoney(value.max),
});

const share = (amount: number, net: number): number => (amount / net) * 100;

/** Asks the door once, with the dealer's typed words, and answers what the screen draws. */
export async function runEstimate(
  inputs: EstimateInputs,
  door: CapacityEstimateDoor,
): Promise<EstimateOutcome> {
  const net = parseAmount(inputs.netSalary);
  if (net === null) return { kind: "need", missing: "netSalary" };
  const typedCommitments = inputs.commitments.trim() === "" ? 0 : parseAmount(inputs.commitments);
  if (typedCommitments === null) return { kind: "need", missing: "commitments" };

  const body: CapacityEstimateBody = {
    netSalaryAmount: net,
    ...(inputs.commitments.trim() === "" ? {} : { monthlyCommitmentsAmount: typedCommitments }),
    depositPct: inputs.deposit,
    durationMonths: inputs.durationMonths,
  };

  let raw: unknown;
  try {
    raw = await door(body);
  } catch (failure) {
    // The door's own refusal of a body (its sentence, the field it names) is drawn as sent; every other failure is a failed read.
    if (failure instanceof ApiError && failure.status === 400 && failure.code === "invalid_request_body") {
      // The door's own shape: `details: { fields: [{ field, reason }] }`, the first field it names.
      const fields = (failure.details as { fields?: unknown } | null | undefined)?.fields;
      const first = Array.isArray(fields) ? (fields[0] as { field?: unknown } | undefined) : undefined;
      return {
        kind: "refused",
        words: failure.message,
        field: typeof first?.field === "string" ? first.field : null,
      };
    }
    return { kind: "failed" };
  }

  const read = capacityEstimateLenientReadSchema.safeParse(raw);
  if (!read.success) return { kind: "failed" };
  const answer = read.data;

  const base: EstimateBase = {
    chip: answer.kind,
    depositPct: answer.depositPct,
    durationMonths: answer.durationMonths,
    caveat: answer.caveat,
    assumptions: answer.assumptions.map((one) => ({
      key: one.key,
      label: one.label,
      value: one.value,
      source: one.source,
    })),
  };

  if (answer.state === "no_capacity") {
    return {
      kind: "no_capacity",
      view: {
        ...base,
        capacity: formatMoney(answer.capacityAmount),
        shortfall: formatMoney(answer.shortfallAmount),
      },
    };
  }

  const figures: Figures = {
    ...base,
    financeable: range(answer.financeableAmount),
    monthly: range(answer.monthlyAmount),
    ratePct: { best: answer.ratePct.best, worst: answer.ratePct.worst },
    ceiling: { min: answer.ceilingPctRange.min, max: answer.ceilingPctRange.max },
    bar: {
      commitmentsShare: share(typedCommitments, net),
      monthlyMinShare: share(answer.monthlyAmount.min, net),
      monthlyMaxShare: share(answer.monthlyAmount.max, net),
      ceilingMarks:
        answer.ceilingPctRange.min === answer.ceilingPctRange.max
          ? [answer.ceilingPctRange.min]
          : [answer.ceilingPctRange.min, answer.ceilingPctRange.max],
    },
    withoutRate: answer.cars.withoutRate.map((one) => ({
      vehicleId: one.vehicleId,
      title: one.title,
      price: formatMoney(one.priceAmount),
      sentence: one.sentence,
    })),
  };

  if (answer.state === "estimate") {
    return {
      kind: "estimate",
      view: {
        ...figures,
        cars: {
          kind: "within",
          parkCount: answer.cars.parkCount,
          within: answer.cars.within.map((one) => ({
            vehicleId: one.vehicleId,
            title: one.title,
            price: formatMoney(one.priceAmount),
            monthly: range(one.monthlyEstimate),
            effortBand: one.effortBand,
            effortBandLabel: one.effortBandLabel,
            resteAVivre: range(one.resteAVivre),
          })),
        },
      },
    };
  }
  return {
    kind: "no_car_within_range",
    view: {
      ...figures,
      cars:
        answer.cars.parkCount === 0
          ? { kind: "empty_park" }
          : { kind: "none_within", parkCount: answer.cars.parkCount },
    },
  };
}

// The body schema is the door's: re-exported so the session that wires the door parses a body with the same definition (A48).
export { capacityEstimateBodySchema };
